import type { ApplicationContext, Log } from '@hc-frontend/core-context';
import { DomainIds } from '@hc-frontend/core-types';
import {
  createSuccessOperationResult,
  type IErrorOperationResult,
  type ISuccessOperationResult,
} from '@hc-frontend/core-utils-business-operation';

import type { TrackingSessionRepository } from '../../repositories';

export type GetDomainIdsUseCase = () => Promise<
  ISuccessOperationResult<Partial<DomainIds>>
>;

export type GetDomainIdsFactoryParams = {
  log: ApplicationContext['log'];
  sessionProvider: TrackingSessionRepository;
};

function logOperation<T extends IErrorOperationResult<string>>(
  log: ApplicationContext['log'],
  result: T,
  type: Log['type'] = 'error',
): T {
  log({
    type,
    context: 'server',
    message: result.errors[0].code,
    data: {
      rootCause: result.errors[0].rootCause,
    },
  });

  return result;
}

const healthcareUserCookieIsPresent = async () => {
  // Check if the Healthcare healthcareUser cookie is present, as a proxy to
  // determine if this is the user's first navigation to our site

  // On the user's first navigation to our site, we expect them to not have the
  // Snowplow cookies created, since that is a frontend-exclusive process.
  const { cookies } = await import('next/headers');
  const cookiesList = (await cookies()).getAll();
  return cookiesList.find((cookie) => cookie.name === 'healthcareUser');
};

export function getDomainIdsFactory({
  log,
  sessionProvider,
}: GetDomainIdsFactoryParams): GetDomainIdsUseCase {
  return async () => {
    const domainIds: Partial<DomainIds> = {};

    const userId = await sessionProvider.getUserId();

    if (!userId.success) {
      if (await healthcareUserCookieIsPresent()) {
        logOperation(log, userId);
      }
    } else {
      domainIds.userId = userId.data;
    }

    const sessionId = await sessionProvider.getSessionId();

    if (!sessionId.success) {
      if (await healthcareUserCookieIsPresent()) {
        logOperation(log, sessionId);
      }
    } else {
      domainIds.sessionId = sessionId.data;
    }

    const parsedDomainIds = DomainIds.partial().safeParse(domainIds);

    if (!parsedDomainIds.success) {
      logOperation(log, {
        success: false,
        errors: [
          {
            code: 'failedToParseDomainIds',
            rootCause: new Error(parsedDomainIds.error.message),
          },
        ],
      });
    } else {
      return createSuccessOperationResult(parsedDomainIds.data);
    }

    return createSuccessOperationResult({});
  };
}
